import { useEffect } from 'react';
import { Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { getStartParam } from './lib/telegram';
import { Home } from './pages/Home';
import { Categories } from './pages/Categories';
import { Topics } from './pages/Topics';
import { Premium } from './pages/Premium';
import { Test } from './pages/Test';
import { Exam } from './pages/Exam';
import { Result } from './pages/Result';
import { Profile } from './pages/Profile';
import { Leaderboard } from './pages/Leaderboard';
import { Login } from './pages/Login';
import { Battles } from './pages/Battles';
import { BattleNew } from './pages/BattleNew';
import { Battle } from './pages/Battle';
import { Referral } from './pages/Referral';
import { Signs } from './pages/Signs';
import { TabBar } from './components/TabBar';
import { Sidebar } from './components/Sidebar';
import { AdminLayout } from './pages/admin/Layout';
import { AdminDashboard } from './pages/admin/Dashboard';
import { AdminBroadcast } from './pages/admin/Broadcast';
import { AdminQuestions } from './pages/admin/Questions';
import { AdminQuestionForm } from './pages/admin/QuestionForm';
import { AdminMarkCorrect } from './pages/admin/MarkCorrect';
import { AdminCategories } from './pages/admin/Categories';
import { AdminUsers } from './pages/admin/Users';
import { AdminSettings } from './pages/admin/Settings';
import { AdminPurchases } from './pages/admin/Purchases';
import { AdminPromocodes } from './pages/admin/Promocodes';
import { AdminQuiz } from './pages/admin/Quiz';
import { AdminFeedback } from './pages/admin/Feedback';
import { AdminExplanations } from './pages/admin/Explanations';
import { AdminReferrals } from './pages/admin/Referrals';
import { isAuthenticated, isMiniApp } from './lib/auth';
import { silentResubscribe } from './lib/push';
import { OfflineBootstrap } from './components/OfflineBootstrap';
import { AppBonus } from './components/AppBonus';

export function App() {
  const location = useLocation();
  const navigate = useNavigate();
  const onLoginPage = location.pathname === '/login';
  const authed = isAuthenticated();

  // Deep-link від бота: /start battle_<code> або battles_new передає start_param,
  // ми перенаправляємо на відповідну сторінку. Робимо ОДИН раз — на mount.
  useEffect(() => {
    const param = getStartParam();
    if (!param) return;
    const battle = param.match(/^battle_([A-Z0-9]+)$/i);
    if (battle) navigate(`/battles/${battle[1]!.toUpperCase()}`, { replace: true });
    else if (param === 'battles_new') navigate('/battles/new', { replace: true });
    // Кнопка «Моє посилання» з розсилок/конкурсу — одразу на сторінку рефералів
    else if (param === 'referral') navigate('/referral', { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Після входу — ТИХО оновлюємо push-підписку, якщо дозвіл уже надано раніше.
  // Промпт дозволу НІКОЛИ не показуємо автоматично — тільки з кнопки (жест юзера).
  useEffect(() => {
    if (!authed) return;
    void silentResubscribe();
  }, [authed]);

  // Якщо не авторизований і не на /login — кидаємо на /login
  if (!authed && !onLoginPage) {
    return <Navigate to="/login" replace />;
  }
  // Якщо вже авторизований і завітав на /login — на головну
  if (authed && onLoginPage) {
    return <Navigate to="/" replace />;
  }

  // На сторінці тесту, битви, адмінки і логіну нижній TabBar тільки відволікає
  const hideTabBar =
    onLoginPage ||
    location.pathname.startsWith('/test/') ||
    location.pathname.startsWith('/exam/') ||
    location.pathname.startsWith('/battles/') ||
    location.pathname.startsWith('/admin');

  // Веб-режим (поза Telegram): на десктопі показуємо бічну панель замість нижніх табів.
  const isWeb = !isMiniApp();

  return (
    <div
      className="flex min-h-full bg-tg-bg text-tg-text"
      style={
        isWeb
          ? {
              background:
                'radial-gradient(800px 480px at 100% 0%, rgba(59,130,246,.06), transparent 65%), var(--tg-bg, #0f172a)',
            }
          : undefined
      }
    >
      {/* Офлайн: готуємо локальну базу питань (оверлей лише при першому завантаженні) */}
      {authed && !onLoginPage && <OfflineBootstrap />}

      {/* Дарунок за встановлення додатка: +3 дні Premium (один раз) */}
      {authed && !onLoginPage && <AppBonus />}

      {/* Десктопна бічна навігація — лише у веб-режимі (поза Telegram), на широких екранах */}
      {isWeb && !hideTabBar && <Sidebar />}

      <div className="flex min-w-0 flex-1 flex-col">
        {/* На мобілці/Telegram — вузька колонка; у веб-десктопі — ширший контент */}
        <main
          className={`mx-auto w-full flex-1 ${
            isWeb
              ? 'max-w-screen-sm lg:max-w-3xl lg:px-6 lg:py-3'
              : 'max-w-screen-sm md:border-x md:border-tg-secondary-bg md:shadow-xl md:shadow-black/30'
          } ${hideTabBar ? '' : isWeb ? 'pb-20 lg:pb-8' : 'pb-20'}`}
        >
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/" element={<Home />} />
          <Route path="/categories" element={<Categories />} />
          <Route path="/topics" element={<Topics />} />
          <Route path="/test/:attemptId" element={<Test />} />
          <Route path="/exam/:attemptId" element={<Exam />} />
          <Route path="/result/:attemptId" element={<Result />} />
          <Route path="/profile" element={<Profile />} />
          <Route path="/premium" element={<Premium />} />
          <Route path="/leaderboard" element={<Leaderboard />} />

          <Route path="/battles" element={<Battles />} />
          <Route path="/battles/new" element={<BattleNew />} />
          <Route path="/battles/:code" element={<Battle />} />
          <Route path="/referral" element={<Referral />} />
          <Route path="/signs" element={<Signs />} />

          <Route path="/admin" element={<AdminLayout />}>
            <Route index element={<Navigate to="/admin/dashboard" replace />} />
            <Route path="dashboard" element={<AdminDashboard />} />
            <Route path="broadcast" element={<AdminBroadcast />} />
            <Route path="questions" element={<AdminQuestions />} />
            <Route path="questions/mark" element={<AdminMarkCorrect />} />
            <Route path="questions/new" element={<AdminQuestionForm />} />
            <Route path="questions/:id" element={<AdminQuestionForm />} />
            <Route path="categories" element={<AdminCategories />} />
            <Route path="users" element={<AdminUsers />} />
            <Route path="settings" element={<AdminSettings />} />
            <Route path="purchases" element={<AdminPurchases />} />
            <Route path="promocodes" element={<AdminPromocodes />} />
            <Route path="quiz" element={<AdminQuiz />} />
            <Route path="feedback" element={<AdminFeedback />} />
            <Route path="explanations" element={<AdminExplanations />} />
            <Route path="referrals" element={<AdminReferrals />} />
          </Route>

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </main>
      </div>
      {/* Нижні таби: на мобілці завжди; у веб-десктопі ховаються (їх замінює сайдбар) */}
      {!hideTabBar &&
        (isWeb ? (
          <div className="lg:hidden">
            <TabBar />
          </div>
        ) : (
          <TabBar />
        ))}
    </div>
  );
}
